import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Megaphone, Wrench, Sparkles, Settings, Loader2, Send } from "lucide-react";
import aapi, { formatApiError } from "./aapi";
import { PageHeader, inputCls } from "@/panel/components";
import { EASE } from "@/components/Motion";

const KINDS = [
  { id: "announcement", label: "Announcement", icon: Megaphone },
  { id: "maintenance", label: "Maintenance Notice", icon: Wrench },
  { id: "promotional", label: "Promotional", icon: Sparkles },
  { id: "system", label: "System", icon: Settings },
];

export default function AdminBroadcast() {
  const [kind, setKind] = useState("announcement");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [target, setTarget] = useState("all");
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (target === "selected" && users.length === 0)
      aapi.get("/users").then(({ data }) => setUsers(data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target]);

  const toggleUser = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await aapi.post("/broadcast", {
        kind, title, body, target, user_ids: target === "selected" ? selected : [],
      });
      toast.success(`Notification sent to ${data.sent} user${data.sent === 1 ? "" : "s"}`, {
        description: "Logged in the audit trail.",
      });
      setTitle("");
      setBody("");
      setSelected([]);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-broadcast-page">
      <PageHeader title="Broadcast Notifications" sub="Push announcements, promos and alerts straight into every customer's panel." />

      <div className="grid gap-6 lg:grid-cols-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
          className="rounded-2xl border border-white/10 bg-panel/80 p-6 sm:p-8 lg:col-span-7">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Notification type</div>
          <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {KINDS.map((k) => (
              <button key={k.id} onClick={() => setKind(k.id)} data-testid={`broadcast-kind-${k.id}`}
                className={`flex flex-col items-center gap-2 rounded-xl border px-3 py-4 text-xs font-medium transition-all duration-300 ${
                  kind === k.id
                    ? "border-gold bg-gold/10 text-goldlight"
                    : "border-white/10 text-neutral-400 hover:border-gold/40 hover:text-neutral-100"
                }`}>
                <k.icon size={16} />
                {k.label}
              </button>
            ))}
          </div>

          <div className="mt-6 space-y-4">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Weekend flash sale is live"
                className={`${inputCls} mt-2`} data-testid="broadcast-title-input" />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Message</label>
              <textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)}
                placeholder="Write the message customers will read in their notifications tab."
                className={`${inputCls} mt-2 resize-none`} data-testid="broadcast-body-input" />
            </div>
          </div>

          <div className="mt-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Target</div>
            <div className="mt-3 flex gap-2">
              {["all", "selected"].map((t) => (
                <button key={t} onClick={() => setTarget(t)} data-testid={`broadcast-target-${t}`}
                  className={`rounded-full border px-5 py-2.5 text-xs font-semibold capitalize transition-all duration-300 ${
                    target === t
                      ? "border-gold bg-gold/10 text-goldlight"
                      : "border-white/10 text-neutral-400 hover:border-gold/40"
                  }`}>
                  {t === "all" ? "All users" : "Selected users"}
                </button>
              ))}
            </div>
          </div>

          {target === "selected" && (
            <div className="mt-4 max-h-56 space-y-1 overflow-y-auto rounded-xl border border-white/10 p-2" data-testid="broadcast-user-list">
              {users.map((u) => (
                <label key={u.id} className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition-colors hover:bg-white/[0.03]">
                  <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggleUser(u.id)}
                    data-testid={`broadcast-user-${u.email}`}
                    className="h-4 w-4 accent-gold" />
                  <span className="text-sm text-neutral-200">{u.name}</span>
                  <span className="truncate font-mono text-[11px] text-neutral-500">{u.email}</span>
                </label>
              ))}
              {users.length === 0 && <p className="px-3 py-4 text-sm text-neutral-500">Loading users…</p>}
            </div>
          )}

          <button onClick={send} disabled={busy || title.trim().length < 3 || body.trim().length < 3 || (target === "selected" && selected.length === 0)}
            data-testid="broadcast-send-button"
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-br from-goldlight via-gold to-golddark py-4 text-sm font-bold tracking-[0.12em] text-black shadow-[0_8px_30px_rgb(var(--gold)_/_0.25)] transition-shadow duration-300 hover:shadow-[0_8px_40px_rgb(var(--gold)_/_0.45)] disabled:cursor-not-allowed disabled:opacity-40">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
            SEND NOTIFICATION
          </button>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE, delay: 0.12 }}
          className="lg:col-span-5">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">Preview — as seen in the customer panel</div>
          <div className="mt-3 rounded-2xl border border-gold/25 bg-gold/[0.05] p-5" data-testid="broadcast-preview">
            <div className="flex gap-4">
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-gold shadow-[0_0_8px_rgb(var(--gold)_/_0.8)]" />
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-neutral-50">{title || "Notification title"}</span>
                  <span className="rounded-full border border-gold/40 px-2 py-0.5 font-mono text-[8px] uppercase tracking-wider text-gold">
                    {kind}
                  </span>
                </div>
                <p className="mt-1 text-sm leading-relaxed text-neutral-400">{body || "Your message preview appears here as you type."}</p>
                <div className="mt-2 font-mono text-[10px] tracking-[0.15em] text-neutral-600">
                  {target === "all" ? "TO: ALL USERS" : `TO: ${selected.length} SELECTED`}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
